/**
 * @typedef {import('App/Admin/Groups/EditGroupTheme/Props').default} Props
 * @typedef {import('shared/ThemeService/Theme').default} Theme
 * @typedef {import('shared/ThemeService/ThemeColors').default} ThemeColors
 */

import { themeService } from 'service-container';
import { translate } from 'App/Internationalization';
import EditTheme from 'App/shared/EditTheme';
import flags from '../../../../shared/Flags';
import Loading from 'shared/Loading';
import NoContent from '../../../../shared/NoContent';
import React from 'react';
import SponsorForm from './SponsorForm';
import captureError from '../../../../utils/captureError';
import useGetGroup from '../../../../Hooks/Admin/Getters/useGetGroup/useGetGroup';
import useIsGated from '../../../../Hooks/Shared/useIsGated/useIsGated';
import useSettings from '../../../../Hooks/Shared/useSettings/useSettings';
import paths from '../../../../config/local/paths';

const defaultGroupName = translate('global.group');

/**
 * @param {{isGated: boolean}} brandingGate
 * @param {{isGated: boolean}} sponsorGate
 * @returns {boolean}
 */
function bothSectionsAreGated(brandingGate, sponsorGate) {
	return brandingGate.isGated && sponsorGate.isGated;
}

/**
 * @param {boolean} isRefreshingSubscriptions
 * @param {{isGettingData: boolean}[]} requests
 * @returns {boolean}
 */
function isPageLoading(isRefreshingSubscriptions, requests) {
	return (
		isRefreshingSubscriptions ||
		requests.some((request) => request.isGettingData)
	);
}

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
export function EditGroupTheme(props) {
	const groupId = props.match.params.groupId;
	const { setSubscriptionFlags } = useSettings();
	const [isRefreshingSubscriptions, setIsRefreshingSubscriptions] =
		React.useState(true);
	const gated = useIsGated(flags.branding.colorLogos, { groupId });
	const sponsorGated = useIsGated(flags.manager.coSponsor, { groupId });
	const group = useGetGroup(
		groupId,
		bothSectionsAreGated(gated, sponsorGated)
	);

	React.useEffect(() => {
		let isMounted = true;

		async function refreshSubscriptionFlags() {
			try {
				await setSubscriptionFlags();
			} catch (error) {
				captureError(error);
			} finally {
				if (isMounted) {
					setIsRefreshingSubscriptions(false);
				}
			}
		}

		refreshSubscriptionFlags();

		return () => {
			isMounted = false;
		};
	}, [setSubscriptionFlags]);

	/** @returns {Promise<Theme>} */
	function getTheme() {
		return themeService.getGroupTheme(groupId);
	}

	/**
	 * @param {File} favicon
	 * @returns {Promise<void>}
	 */
	function setFavicon(favicon) {
		return themeService.setGroupFavicon(groupId, favicon);
	}

	/**
	 * @param {File} logo
	 * @returns {Promise<void>}
	 */
	function setLogo(logo) {
		return themeService.setGroupLogo(groupId, logo);
	}

	/**
	 * @param {ThemeColors} colors
	 * @returns {Promise<void>}
	 */
	function setTheme(colors) {
		return themeService.setGroupTheme(groupId, colors);
	}

	if (
		isPageLoading(isRefreshingSubscriptions, [gated, sponsorGated, group])
	) {
		return <Loading />;
	}

	if (group.isError) {
		return (
			<NoContent
				message={translate('admin.pages.import_agents.group_not_found')}
			/>
		);
	}

	const subscriptionLink = `${paths.app.admin.groups.root}/${groupId}/subscription`;

	return (
		<React.Fragment>
			<EditTheme
				getTheme={getTheme}
				heading={translate(
					'global.header.edit_branding',
					group.data?.name || defaultGroupName
				)}
				isGated={gated.isGated}
				isGettingData={gated.isGettingData}
				setFavicon={setFavicon}
				setLogo={setLogo}
				setTheme={setTheme}
				subscriptionLink={subscriptionLink}
				userType="administrator"
			/>
			<SponsorForm
				groupId={groupId}
				isGated={sponsorGated.isGated}
				subscriptionLink={subscriptionLink}
			/>
		</React.Fragment>
	);
}

export default React.memo(EditGroupTheme);
